import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Phone, Mail, MapPin, Clock, Award, Users, Calendar } from 'lucide-react';

const AboutPage = () => {
  const stats = [
    { icon: Calendar, value: '10+', label: 'Years Experience' },
    { icon: Users, value: '500+', label: 'Happy Clients' },
    { icon: Award, value: '50+', label: 'Projects Completed' },
  ];

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#FAF9F6]">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs uppercase tracking-[0.3em] text-[#C5A065] mb-4 block">
                About Us
              </span>
              <h1
                data-testid="about-page-title"
                className="font-heading text-4xl md:text-5xl text-[#2D2D2D] mb-6"
              >
                Decors Genius
              </h1>
              <p className="text-[#6E6E6E] text-lg leading-relaxed mb-6">
                We are a New York-based home decoration company dedicated to
                transforming residential spaces into elegant, personalized
                sanctuaries. Our team combines design expertise with a genuine
                understanding of how people live in their homes.
              </p>
              <p className="text-[#6E6E6E] leading-relaxed">
                Founded on the principles of thoughtful design and transparent
                communication, we work closely with each client to create spaces
                that reflect their unique style and enhance their daily lives.
              </p>
            </div>
            <div className="image-hover-zoom">
              <img
                src="https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&w=1200&q=80"
                alt="Decors Genius Team"
                className="w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white border-y border-[#E5E0D8]">
        <div className="container-main">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                data-testid={`about-stat-${index}`}
                className="text-center"
              >
                <div className="w-14 h-14 rounded-full bg-[#FAF9F6] flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="w-6 h-6 text-[#C5A065]" />
                </div>
                <div className="font-heading text-4xl text-[#2D2D2D] mb-2">
                  {stat.value}
                </div>
                <div className="text-[#6E6E6E] text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="section-padding bg-[#FAF9F6]">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <span className="text-xs uppercase tracking-[0.3em] text-[#C5A065] mb-4 block">
              Our Values
            </span>
            <h2 className="font-heading text-3xl md:text-4xl text-[#2D2D2D] mb-6">
              What We Stand For
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Transparency',
                description:
                  'Clear communication, honest pricing, and no hidden surprises. We believe trust is built through openness.',
              },
              {
                title: 'Quality',
                description:
                  'We source premium materials and work with skilled professionals to ensure lasting results.',
              },
              {
                title: 'Personalization',
                description:
                  'Every project is unique. We take time to understand your vision and create spaces that truly reflect you.',
              },
            ].map((value, index) => (
              <div key={index} className="bg-white p-8">
                <h3 className="font-heading text-xl text-[#2D2D2D] mb-4">
                  {value.title}
                </h3>
                <p className="text-[#6E6E6E] leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Info */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <span className="text-xs uppercase tracking-[0.3em] text-[#C5A065] mb-4 block">
                Business Information
              </span>
              <h2 className="font-heading text-3xl text-[#2D2D2D] mb-8">
                Contact Details
              </h2>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#FAF9F6] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#C5A065]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-[#2D2D2D] mb-1">Location</h4>
                    <p className="text-[#6E6E6E]">New York, USA</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#FAF9F6] flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#C5A065]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-[#2D2D2D] mb-1">Phone</h4>
                    <a
                      href="tel:+18888801455"
                      data-testid="about-phone"
                      className="text-[#6E6E6E] hover:text-[#C5A065] transition-colors"
                    >
                      +1-888-880-1455
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#FAF9F6] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#C5A065]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-[#2D2D2D] mb-1">Email</h4>
                    <a
                      href="mailto:support@decorsgenius.store"
                      data-testid="about-email"
                      className="text-[#6E6E6E] hover:text-[#C5A065] transition-colors"
                    >
                      support@decorsgenius.store
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#FAF9F6] flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-[#C5A065]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-[#2D2D2D] mb-1">
                      Business Hours
                    </h4>
                    <p className="text-[#6E6E6E]">
                      Monday - Saturday: 9:00 AM - 6:00 PM
                    </p>
                    <p className="text-[#6E6E6E]">Sunday: Closed</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#FAF9F6] p-8 md:p-12">
              <h3 className="font-heading text-2xl text-[#2D2D2D] mb-6">
                Ready to Transform Your Space?
              </h3>
              <p className="text-[#6E6E6E] mb-8 leading-relaxed">
                We'd love to hear about your project. Schedule a complimentary
                consultation to discuss your vision and how we can help bring it
                to life.
              </p>
              <Link to="/contact">
                <Button
                  data-testid="about-cta-btn"
                  className="btn-primary w-full md:w-auto"
                >
                  Schedule Consultation
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
